import React, { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import {
  Box, Typography, Table, TableHead, TableBody, TableRow, TableCell,
  Checkbox, TextField, Divider,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";

const TEMPLATE = [
  { id: "cardiovascular", label: "Enfermedades Cardiovasculares", sublabel: "HTA, IM, Vasculitis, Insuficiencias" },
  { id: "diabetes",       label: "Diabetes",                       sublabel: "" },
  { id: "mentales",       label: "Enfermedades mentales/psicológicas", sublabel: "epilepsia, convulsiones" },
  { id: "cancer",         label: "Cáncer",                         sublabel: "" },
  { id: "nefropatias",    label: "Nefropatías",                    sublabel: "" },
  { id: "hepatopatias",   label: "Hepatopatías",                   sublabel: "" },
  { id: "tuberculosis",   label: "Tuberculosis",                   sublabel: "" },
  { id: "asma",           label: "Asma bronquial",                 sublabel: "" },
  { id: "alergicas",      label: "Enfermedades alérgicas",         sublabel: "" },
];

const buildRows = (saved) => {
  let parsed = saved;
  if (typeof saved === "string") {
    try { parsed = JSON.parse(saved); } catch { parsed = []; }
  }
  const list = Array.isArray(parsed) ? parsed : [];
  return TEMPLATE.map((t) => {
    const existing = list.find((r) => r.id === t.id);
    return { ...t, padre: false, madre: false, otro: "", descripcion: "", ...existing };
  });
};

const getOtroTexto = (saved) => {
  let parsed = saved;
  if (typeof saved === "string") {
    try { parsed = JSON.parse(saved); } catch { parsed = []; }
  }
  const list = Array.isArray(parsed) ? parsed : [];
  return list.find((r) => r.id === "otro")?.descripcion || "";
};

const hasData = (rows, otroTexto) => rows.some((r) => r.padre || r.madre || r.otro) || !!otroTexto;

const AntecedentesFamiliares = forwardRef(({ patient, editing }, ref) => {
  const [editRows, setEditRows] = useState(() => buildRows(patient?.antecedentesFamiliares));
  const [otroTexto, setOtroTexto] = useState(() => getOtroTexto(patient?.antecedentesFamiliares));

  useEffect(() => {
    if (editing) {
      setEditRows(buildRows(patient?.antecedentesFamiliares));
      setOtroTexto(getOtroTexto(patient?.antecedentesFamiliares));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  useImperativeHandle(ref, () => ({
    getPayload: () => {
      const rows = editRows.filter((r) => r.padre || r.madre || r.otro || r.descripcion);
      if (otroTexto) rows.push({ id: "otro", descripcion: otroTexto });
      return rows;
    },
  }));

  const updateRow = (id, field, value) => {
    setEditRows((prev) => prev.map((r) => r.id === id ? { ...r, [field]: value } : r));
  };

  const viewRows = buildRows(patient?.antecedentesFamiliares);
  const filled = viewRows.filter((r) => r.padre || r.madre || r.otro);
  const viewOtroTexto = getOtroTexto(patient?.antecedentesFamiliares);

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>
        Antecedentes familiares
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        ¿Alguno de sus familiares ha padecido o padece alguna de las siguientes enfermedades?
      </Typography>
      <Divider sx={{ mb: 2 }} />

      {editing ? (
        <>
          <Table size="small" sx={{ "& td, & th": { py: 0.5, px: 0.75 } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: "40%" }}>Enfermedad</TableCell>
                <TableCell sx={{ width: "10%" }} align="center">Padre</TableCell>
                <TableCell sx={{ width: "10%" }} align="center">Madre</TableCell>
                <TableCell sx={{ width: "40%" }}>Otro familiar</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {editRows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <Typography variant="body2">{row.label}</Typography>
                    {row.sublabel && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>{row.sublabel}</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox size="small" checked={!!row.padre} onChange={(e) => updateRow(row.id, "padre", e.target.checked)} />
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox size="small" checked={!!row.madre} onChange={(e) => updateRow(row.id, "madre", e.target.checked)} />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small" fullWidth variant="standard"
                      value={row.otro}
                      onChange={(e) => updateRow(row.id, "otro", e.target.value)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Otro</Typography>
            <TextField
              size="small" fullWidth multiline minRows={2} variant="outlined"
              placeholder="Otros antecedentes familiares..."
              value={otroTexto}
              onChange={(e) => setOtroTexto(e.target.value)}
            />
          </Box>
        </>
      ) : !hasData(viewRows, viewOtroTexto) ? (
        <Typography variant="body2" color="text.secondary">Sin antecedentes familiares registrados.</Typography>
      ) : (
        <>
        <Table size="small" sx={{ "& td, & th": { py: 0.5, px: 0.75 } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "40%" }}>Enfermedad</TableCell>
              <TableCell sx={{ width: "10%" }} align="center">Padre</TableCell>
              <TableCell sx={{ width: "10%" }} align="center">Madre</TableCell>
              <TableCell sx={{ width: "40%" }}>Otro familiar</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filled.map((row) => (
              <TableRow key={row.id}>
                <TableCell>
                  <Typography variant="body2">{row.label}</Typography>
                  {row.sublabel && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>{row.sublabel}</Typography>
                  )}
                  {row.descripcion && (
                    <Typography variant="caption" sx={{ display: "block", fontStyle: "italic" }}>{row.descripcion}</Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  {row.padre ? <CheckIcon fontSize="small" color="primary" /> : <Typography variant="body2" color="text.disabled">—</Typography>}
                </TableCell>
                <TableCell align="center">
                  {row.madre ? <CheckIcon fontSize="small" color="primary" /> : <Typography variant="body2" color="text.disabled">—</Typography>}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{row.otro || "—"}</Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {viewOtroTexto && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Otro</Typography>
            <Typography variant="body2">{viewOtroTexto}</Typography>
          </Box>
        )}
        </>
      )}
    </Box>
  );
});

export default AntecedentesFamiliares;
